import { useState, useEffect, useCallback } from 'react';
import { API } from '../../config/api';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Users, UserPlus, Trash2, Save, Loader2, Shield, Copy, ChevronDown, ChevronUp } from 'lucide-react';

const PERM_GROUPS = [
  { label: 'Creative Tools', perms: [
    { id: 'beat-station', label: 'Beat Station' },
    { id: 'animation', label: 'Animation Studio' },
    { id: 'mastering', label: 'Mastering Station' },
    { id: 'artwork', label: 'Artwork Studio' },
  ]},
  { label: 'Content Management', perms: [
    { id: 'tracks', label: 'Tracks' },
    { id: 'beats', label: 'Beat Licensing' },
    { id: 'merch', label: 'Merch' },
    { id: 'videos', label: 'Videos' },
    { id: 'free-media', label: 'Free Media' },
    { id: 'albums', label: 'Albums' },
  ]},
  { label: 'Site & Settings', perms: [
    { id: 'analytics', label: 'Analytics' },
    { id: 'subscribers', label: 'Subscribers' },
    { id: 'site', label: 'Site Settings' },
    { id: 'theme', label: 'Theme' },
    { id: 'customize', label: 'Customize' },
    { id: 'discounts', label: 'Discounts' },
    { id: 'publishing', label: 'Publishing' },
    { id: 'donations', label: 'Donations' },
    { id: 'featured', label: 'Featured Artists' },
    { id: 'promos', label: 'Promos' },
    { id: 'mockups', label: 'Mockups' },
    { id: 'scheduled', label: 'Scheduled' },
  ]},
];

export default function TeamPanel() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [invitePerms, setInvitePerms] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [expandedMember, setExpandedMember] = useState(null);
  const [savingPerms, setSavingPerms] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [lastTempPw, setLastTempPw] = useState(null);

  const fetchMembers = useCallback(async () => {
    try {
      const { data } = await api.get(`${API}/team/members`, { withCredentials: true });
      setMembers(data);
    } catch { toast.error('Failed to load team'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const toggleInvitePerm = (perm) => {
    setInvitePerms(prev => prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]);
  };

  const selectAllInvitePerms = () => {
    const all = PERM_GROUPS.flatMap(g => g.perms.map(p => p.id));
    setInvitePerms(prev => prev.length === all.length ? [] : all);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return toast.error('Email required');
    setInviting(true);
    try {
      const { data } = await api.post(`${API}/team/invite`, {
        email: inviteEmail.trim(),
        name: inviteName.trim(),
        permissions: invitePerms,
      }, { withCredentials: true });
      if (data.temp_password) {
        setLastTempPw({ email: inviteEmail, password: data.temp_password });
      }
      toast.success(data.status === 'promoted' ? 'User promoted to collaborator!' : 'Invitation sent!');
      setInviteEmail('');
      setInviteName('');
      setInvitePerms([]);
      setShowInvite(false);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invite failed');
    }
    setInviting(false);
  };

  const updateMemberPerms = async (email, permissions) => {
    setSavingPerms(email);
    try {
      await api.put(`${API}/team/members/${email}/permissions`, { permissions }, { withCredentials: true });
      toast.success('Permissions updated');
      fetchMembers();
    } catch { toast.error('Failed to update'); }
    setSavingPerms(null);
  };

  const removeMember = async (email) => {
    if (!window.confirm(`Remove ${email} from your team?`)) return;
    try {
      await api.delete(`${API}/team/members/${email}`, { withCredentials: true });
      toast.success('Member removed');
      fetchMembers();
    } catch { toast.error('Failed to remove'); }
  };

  const toggleMemberPerm = (member, perm) => {
    const current = member.permissions || [];
    const updated = current.includes(perm) ? current.filter(p => p !== perm) : [...current, perm];
    updateMemberPerms(member.email, updated);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="space-y-6" data-testid="team-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-black uppercase tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            Team
          </h2>
          <p className="text-sm text-[#666] mt-1">Invite up to 10 collaborators with limited admin access</p>
        </div>
        <button onClick={() => setShowInvite(!showInvite)}
          className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white transition-all hover:brightness-110"
          style={{ background: 'var(--primary)', borderRadius: 'var(--radius)', boxShadow: `0 2px 12px rgba(var(--primary-rgb), 0.3)` }}
          data-testid="invite-member-btn">
          <UserPlus className="w-3.5 h-3.5" />
          INVITE MEMBER
        </button>
      </div>

      {/* Temp password display */}
      {lastTempPw && (
        <div className="bg-[#0A0A0A] border border-[#DAA520]/30 p-4" style={{ borderRadius: 'var(--radius)' }}
          data-testid="temp-password-display">
          <p className="text-xs text-[#DAA520] font-bold uppercase mb-2">Temporary Login Credentials</p>
          <p className="text-sm text-white">Share these with <strong>{lastTempPw.email}</strong>:</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="bg-[#1a1a1a] px-3 py-1.5 text-sm font-mono text-white border border-[#333]" style={{ borderRadius: 'var(--radius)' }}>
              {lastTempPw.password}
            </code>
            <button onClick={() => copyToClipboard(lastTempPw.password)}
              className="p-1.5 text-[#888] hover:text-white transition-colors"
              data-testid="copy-temp-pw">
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[10px] text-[#666] mt-2">They can change their password after logging in at /admin</p>
          <button onClick={() => setLastTempPw(null)} className="text-[10px] text-[#888] hover:text-white mt-1 underline">Dismiss</button>
        </div>
      )}

      {/* Invite form */}
      {showInvite && (
        <form onSubmit={handleInvite} className="bg-[#0A0A0A] border border-[#262626] p-5 space-y-4"
          style={{ borderRadius: 'var(--radius)' }} data-testid="invite-form">
          <p className="text-sm font-bold uppercase tracking-wider">New Collaborator</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#888] uppercase tracking-wider block mb-1">Email</label>
              <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm text-white outline-none focus:border-[#444]"
                style={{ borderRadius: 'var(--radius)' }} required placeholder="collab@email.com"
                data-testid="invite-email" />
            </div>
            <div>
              <label className="text-xs text-[#888] uppercase tracking-wider block mb-1">Name</label>
              <input type="text" value={inviteName} onChange={(e) => setInviteName(e.target.value)}
                className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm text-white outline-none focus:border-[#444]"
                style={{ borderRadius: 'var(--radius)' }} placeholder="Their name"
                data-testid="invite-name" />
            </div>
          </div>

          {/* Permission selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-[#888] uppercase tracking-wider">Permissions</label>
              <button type="button" onClick={selectAllInvitePerms}
                className="text-[10px] text-[#DAA520] hover:text-white transition-colors underline"
                data-testid="select-all-perms">
                {invitePerms.length === PERM_GROUPS.flatMap(g => g.perms).length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="space-y-3">
              {PERM_GROUPS.map(group => (
                <div key={group.label}>
                  <p className="text-[10px] text-[#555] uppercase tracking-wider mb-1">{group.label}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {group.perms.map(p => (
                      <button key={p.id} type="button" onClick={() => toggleInvitePerm(p.id)}
                        className={`px-2 py-1 text-[10px] font-bold border transition-colors ${
                          invitePerms.includes(p.id) ? 'border-white/30 bg-white/10 text-white' : 'border-[#262626] text-[#666] hover:text-white'
                        }`}
                        style={{ borderRadius: 'var(--radius)' }}
                        data-testid={`invite-perm-${p.id}`}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <button type="submit" disabled={inviting}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
              style={{ background: 'var(--primary)', borderRadius: 'var(--radius)' }}
              data-testid="send-invite-btn">
              {inviting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
              {inviting ? 'INVITING...' : 'SEND INVITE'}
            </button>
            <button type="button" onClick={() => setShowInvite(false)}
              className="px-4 py-2 text-xs font-bold text-[#666] border border-[#262626] hover:text-white"
              style={{ borderRadius: 'var(--radius)' }}>
              CANCEL
            </button>
          </div>
        </form>
      )}

      {/* Member list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#555]" />
        </div>
      ) : members.length === 0 ? (
        <div className="text-center py-12 border border-[#262626] bg-[#0A0A0A]" style={{ borderRadius: 'var(--radius)' }}>
          <Users className="w-10 h-10 text-[#262626] mx-auto mb-3" />
          <p className="text-sm text-[#666]">No team members yet</p>
          <p className="text-[10px] text-[#444] mt-1">Invite collaborators to give them limited access to your tools</p>
        </div>
      ) : (
        <div className="space-y-2">
          {members.map(member => {
            const isExpanded = expandedMember === member.email;
            return (
              <div key={member.email}
                className="border border-[#262626] bg-[#0A0A0A] overflow-hidden"
                style={{ borderRadius: 'var(--radius)' }}
                data-testid={`member-${member.email}`}>
                {/* Member header */}
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-white/5 flex items-center justify-center text-xs font-bold text-white/50"
                      style={{ borderRadius: 'var(--radius)' }}>
                      {(member.name || member.email)[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-bold">{member.name || member.email.split('@')[0]}</p>
                      <p className="text-[10px] text-[#666]">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#DAA520] font-mono">
                      {(member.permissions || []).length} permissions
                    </span>
                    <button onClick={() => setExpandedMember(isExpanded ? null : member.email)}
                      className="p-1 text-[#666] hover:text-white transition-colors"
                      data-testid={`expand-${member.email}`}>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <button onClick={() => removeMember(member.email)}
                      className="p-1 text-[#666] hover:text-[#FF3B30] transition-colors"
                      data-testid={`remove-${member.email}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Expanded permissions */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-[#1a1a1a]">
                    {savingPerms === member.email && (
                      <div className="flex items-center gap-2 mb-2">
                        <Loader2 className="w-3 h-3 animate-spin text-[#DAA520]" />
                        <span className="text-[10px] text-[#DAA520]">Saving...</span>
                      </div>
                    )}
                    <div className="space-y-3">
                      {PERM_GROUPS.map(group => (
                        <div key={group.label}>
                          <p className="text-[10px] text-[#555] uppercase tracking-wider mb-1">{group.label}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {group.perms.map(p => (
                              <button key={p.id} onClick={() => toggleMemberPerm(member, p.id)}
                                className={`px-2 py-1 text-[10px] font-bold border transition-colors ${
                                  (member.permissions || []).includes(p.id) ? 'border-white/30 bg-white/10 text-white' : 'border-[#262626] text-[#666] hover:text-white'
                                }`}
                                style={{ borderRadius: 'var(--radius)' }}
                                data-testid={`perm-${member.email}-${p.id}`}>
                                {p.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <p className="text-[10px] text-[#444] text-center">{members.length}/10 collaborators</p>
        </div>
      )}
    </div>
  );
}
